import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getPaginated, getOne, post, put } from '@/lib/apiV2';
import { useIsAuthenticated } from '@/features/auth/authV2Store';
import {
  notificationPaths,
  buildRegisterDeviceBody,
  type RegisterDeviceBody,
} from '@/features/notifications/paths';

// ---- CONTRACT — resolved from backend/internal/api/notifications/handler.go
// (not the legacy workwave `/api/notifications` shape). ADR-0007: in-app
// surface + device register use /api/v1/* via apiV2 against
// api.campus.varkwave.com (school demo — not api.us / api.varkwave.com).
//
// 1. GET /notifications (requireAuth, actor-scoped — no extra permission).
// 2. GET /notifications/unread-count => {data:{count:number}}.
// 3. POST /notifications/:id/read — idempotent, empty body/response.
// 4. POST /notifications/read-all — empty body/response.
// 5. PUT /me/device — {fcm_token, platform: 'android'|'ios'|'web'}.
// ============================================================================

export {
  notificationPaths,
  buildRegisterDeviceBody,
  type RegisterDeviceBody,
} from '@/features/notifications/paths';

/** Mirrors notifications.notificationResponse (JSON tags). */
export interface NotificationDTO {
  id: string;
  type: string;
  title: string;
  body: string | null;
  data: Record<string, unknown> | null;
  is_read: boolean;
  read_at: string | null;
  created_at: string;
}

export const notificationKeys = {
  all: ['notifications'] as const,
  list: (page: number, perPage: number, unreadOnly?: boolean) =>
    [...notificationKeys.all, 'list', page, perPage, unreadOnly ?? false] as const,
  unreadCount: () => [...notificationKeys.all, 'unread-count'] as const,
};

// ---- List --------------------------------------------------------------------

export function useNotifications(options: {
  page?: number;
  perPage?: number;
  unreadOnly?: boolean;
} = {}) {
  const { page = 1, perPage = 10, unreadOnly = false } = options;
  const isAuthenticated = useIsAuthenticated();

  return useQuery({
    queryKey: notificationKeys.list(page, perPage, unreadOnly),
    queryFn: () =>
      getPaginated<NotificationDTO>(notificationPaths.list, {
        page,
        per_page: perPage,
        ...(unreadOnly ? { unread: 'true' } : {}),
      }),
    enabled: isAuthenticated,
    staleTime: 1000 * 30,
  });
}

// ---- Unread count --------------------------------------------------------------

export function useUnreadCount() {
  const isAuthenticated = useIsAuthenticated();

  return useQuery({
    queryKey: notificationKeys.unreadCount(),
    queryFn: () => getOne<{ count: number }>(notificationPaths.unreadCount),
    enabled: isAuthenticated,
    refetchInterval: 30000,
    staleTime: 1000 * 15,
  });
}

// ---- Mark one read --------------------------------------------------------------

export function useMarkRead() {
  const queryClient = useQueryClient();

  return useMutation<Record<string, never>, Error, string>({
    mutationFn: (id) =>
      post<Record<string, never>, Record<string, never>>(notificationPaths.markRead(id), {}),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: notificationKeys.all });
    },
  });
}

// ---- Mark all read --------------------------------------------------------------

export function useMarkAllRead() {
  const queryClient = useQueryClient();

  return useMutation<Record<string, never>, Error, void>({
    mutationFn: () =>
      post<Record<string, never>, Record<string, never>>(notificationPaths.markAllRead, {}),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: notificationKeys.all });
    },
  });
}

// ---- Device register (FCM) ----------------------------------------------------

/** `PUT /api/v1/me/device` — registers or refreshes the browser's FCM token. */
export function registerDevice(fcmToken: string): Promise<{ id: string; platform: string }> {
  const body = buildRegisterDeviceBody(fcmToken, 'web');
  return put<RegisterDeviceBody, { id: string; platform: string }>(notificationPaths.device, body);
}
